import { TFunction } from 'i18next';
import { MetricDetails } from '../../../../api/metrics/types';
import { MetricId } from '../../../../api/types-graphql';
import { formatMetricValue } from '../../../../utils-display';
import {
  DataMapValue,
  MultiDatasetChartData,
} from '../../ui-components/data-viz-ui/charts/common/common-chart-types';
import {
  LabelsToBenchmarkDataMap,
  LineChartData,
} from '../../ui-components/data-viz-ui/charts/line-chart/line-chart-types';
import { OverTimeUnsegmentedVizDataResponse } from './types';

export const getLineChartDataFromOverTimeUnsegmentedResponse = <
  HasBenchmark extends boolean,
>(
  response: OverTimeUnsegmentedVizDataResponse<HasBenchmark>,
  metricDisplayValues: Partial<Record<MetricId, string>>,
  metricDetailsMap: Partial<Record<MetricId, MetricDetails>>,
  t: TFunction
): LineChartData => {
  const { responseForMetrics } = response;
  if (responseForMetrics.length === 0) {
    return {
      labels: [],
      dataMap: {},
      datasets: [],
      hasBenchmarkData: false,
    };
  }
  const labels = responseForMetrics[0].data.map((d) => d.dateLabel);

  const dataMap: Record<string, DataMapValue[]> = Object.fromEntries(
    labels.map((label, i) => {
      const responsesForLabel: DataMapValue[] = responseForMetrics.map((r) => ({
        datasetKey: r.metric.value,
        value: r.data[i].datapoint.value,
      }));
      return [label, responsesForLabel];
    })
  );

  const datasets = responseForMetrics.map((r) => ({
    value: r.metric.value,
    label: metricDisplayValues[r.metric.value] ?? r.metric.value,
    valueFormatter: (val: number) => {
      const metricDetails = metricDetailsMap[r.metric.value];
      if (!metricDetails) {
        throw new Error(
          `Invalid state. Metric details undefined for metric: ${r.metric.value}`
        );
      }
      return formatMetricValue(val, metricDetails.formatType);
    },
  }));

  const hasBenchmark = responseForMetrics.first()?.data.first()
    ?.datapoint.hasBenchmark;
  if (!hasBenchmark) {
    return {
      labels,
      dataMap,
      datasets,
      hasBenchmarkData: false,
    };
  } else {
    const labelsToBenchmarkDataMap: Record<string, LabelsToBenchmarkDataMap> =
      Object.fromEntries(
        labels.map((label, i) => {
          const firstDataPoint = responseForMetrics[0].data[i].datapoint;
          // Assuming here that benchmarkDateLabel is the same for all metrics
          const benchmarkDateLabel = firstDataPoint.hasBenchmark
            ? firstDataPoint.benchmarkData.benchmarkDateLabel
            : '';

          return [
            label,
            {
              benchmarkDateLabel,
              data: responseForMetrics.map((r) => {
                const datapoint = r.data[i].datapoint;
                return {
                  datasetKey: r.metric.value,
                  value: datapoint.hasBenchmark
                    ? datapoint.benchmarkData.value
                    : datapoint.value,
                };
              }),
            },
          ];
        })
      );

    return {
      labels,
      dataMap,
      datasets,
      hasBenchmarkData: true,
      benchmarkDatasetLabelFormatter: (label: string) =>
        `${label}(${t('common:commonValues.misc.benchmark')})`,
      labelsToBenchmarkDataMap,
    };
  }
};

export const getBarChartDataFromOverTimeUnsegmentedResponse = (
  response: OverTimeUnsegmentedVizDataResponse<false>,
  metricDisplayValues: Partial<Record<MetricId, string>>,
  metricDetailsMap: Partial<Record<MetricId, MetricDetails>>,
  t: TFunction
): MultiDatasetChartData => {
  const { responseForMetrics } = response;
  if (responseForMetrics.length === 0) {
    return {
      labels: [],
      dataMap: {},
      datasets: [],
      type: 'multi-dataset',
      isStackable: false,
    };
  }
  const labels = responseForMetrics[0].data.map((d) => d.dateLabel);

  const dataMap: Record<string, DataMapValue[]> = Object.fromEntries(
    labels.map((label, i) => {
      const responsesForLabel: DataMapValue[] = responseForMetrics.map((r) => ({
        datasetKey: r.metric.value,
        value: r.data[i].datapoint.value,
      }));
      return [label, responsesForLabel];
    })
  );

  const datasets = responseForMetrics.map((r) => ({
    value: r.metric.value,
    label: metricDisplayValues[r.metric.value] ?? r.metric.value,
    valueFormatter: (val: number) => {
      const metricDetails = metricDetailsMap[r.metric.value];
      if (!metricDetails) {
        throw new Error(
          `Invalid state. Metric details undefined for metric: ${r.metric.value}`
        );
      }
      return formatMetricValue(val, metricDetails.formatType);
    },
  }));

  return {
    labels,
    dataMap,
    datasets,
    type: 'multi-dataset',
    isStackable: false,
  };
};
